import { useEffect, useMemo, useState } from 'react';

import {
    type AdminAgentRunRead,
    type AdminAgentRunSummary,
    type PhoebeAgentRunPendingEventRead,
} from '@apps/web/components/admin/admin_agent_chat_session_types';
import { usePhoebeAgentEventFeedChatViewState } from '@apps/web/components/phoebe_chat_view';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { useOrganizationEvents } from '@apps/web/hooks/use_organization_events';
import { usePhoebeAgentRunEventStream } from '@apps/web/hooks/use_phoebe_agent_run_event_stream';
import { useUser } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type components } from '@libraries/typescript/api_client';

type OptimisticPendingEvent = {
    readonly runId: string;
    readonly pendingEvent: PhoebeAgentRunPendingEventRead;
};

export type AdminAgentTransientProgress = {
    readonly content: string;
    readonly sequence: number;
};

export function useAdminAgentChatStream({
    allowedSelectedRunId,
    isSending,
    selectedOrganizationId,
    selectedRun,
    selectedRunStatus,
    streamOrganizationId,
}: {
    readonly allowedSelectedRunId: string | null;
    readonly isSending: boolean;
    readonly selectedOrganizationId: string;
    readonly selectedRun: AdminAgentRunRead | AdminAgentRunSummary | null;
    readonly selectedRunStatus:
        | components['schemas']['PhoebeAgentRunStatus']
        | null;
    readonly streamOrganizationId?: string | null;
}) {
    const [optimisticPendingEvents, setOptimisticPendingEvents] = useState<
        Array<OptimisticPendingEvent>
    >([]);
    const [transientProgress, setTransientProgress] =
        useState<AdminAgentTransientProgress | null>(null);
    const eventOrganizationId =
        selectedRun?.organization_id ??
        streamOrganizationId ??
        selectedOrganizationId;
    const { data: user } = useUser({ reason: 'admin-agent-events' });
    useOrganizationEvents(eventOrganizationId, user?.id ?? null, {
        notificationChannelEnabled: false,
    });
    const runEventStream = usePhoebeAgentRunEventStream({
        organizationId: eventOrganizationId,
        phoebeAgentRunId: allowedSelectedRunId,
        mode: selectedRun?.mode,
        variant: 'admin',
    });

    useListenForOrganizationEvent(
        'agent_run_stream_event',
        (event) => {
            if (
                event.type !== 'agent_run_stream_event' ||
                event.data.type !== 'activity_update' ||
                event.data.run_id !== allowedSelectedRunId ||
                typeof event.data.content !== 'string' ||
                event.data.content.trim().length === 0
            ) {
                return;
            }
            const content = event.data.content;
            const sequence = event.data.sequence;
            setTransientProgress({ content, sequence });
        },
        { organizationId: eventOrganizationId },
    );

    useEffect(() => {
        setTransientProgress(null);
    }, [allowedSelectedRunId]);

    useEffect(() => {
        const hasTerminalEvent = runEventStream.events.some((event) =>
            [
                'run_completed',
                'run_interrupted',
                'run_cancelled',
                'run_failed',
            ].includes(event.type),
        );
        if (hasTerminalEvent) {
            setTransientProgress(null);
        }
    }, [runEventStream.events]);

    useEffect(() => {
        setOptimisticPendingEvents((current) => {
            const next = current.filter(
                (optimisticEvent) =>
                    optimisticEvent.runId === allowedSelectedRunId,
            );
            return next.length === current.length ? current : next;
        });
    }, [allowedSelectedRunId]);

    useEffect(() => {
        const echoedExternalRefs = new Set(
            runEventStream.pendingEvents.map(
                (pendingEvent) => pendingEvent.external_ref,
            ),
        );
        setOptimisticPendingEvents((current) => {
            const next = current.filter(
                (optimisticEvent) =>
                    !echoedExternalRefs.has(
                        optimisticEvent.pendingEvent.external_ref,
                    ),
            );
            return next.length === current.length ? current : next;
        });
    }, [runEventStream.pendingEvents]);

    const pendingEventsWithOptimisticSends = useMemo(() => {
        const optimisticEventsForRun = optimisticPendingEvents
            .filter(
                (optimisticEvent) =>
                    optimisticEvent.runId === allowedSelectedRunId,
            )
            .map((optimisticEvent) => optimisticEvent.pendingEvent);
        if (optimisticEventsForRun.length === 0) {
            return runEventStream.pendingEvents;
        }
        const echoedExternalRefs = new Set(
            runEventStream.pendingEvents.map(
                (pendingEvent) => pendingEvent.external_ref,
            ),
        );
        return [
            ...runEventStream.pendingEvents,
            ...optimisticEventsForRun.filter(
                (optimisticEvent) =>
                    !echoedExternalRefs.has(optimisticEvent.external_ref),
            ),
        ];
    }, [
        allowedSelectedRunId,
        optimisticPendingEvents,
        runEventStream.pendingEvents,
    ]);

    const chatState = usePhoebeAgentEventFeedChatViewState({
        events: runEventStream.events,
        pendingEvents: pendingEventsWithOptimisticSends,
        isSending,
        fallbackRunStatus: selectedRunStatus,
        phoebeAgentRunId: allowedSelectedRunId,
        showGeneralOutreachProgressCards: true,
        renderToolExecutionsTopLevel: true,
        liveEventIds: runEventStream.liveEventIds,
    });

    return {
        chatState,
        isSelectedRunBusy: chatState.isAgentBusy,
        pendingEvents: pendingEventsWithOptimisticSends,
        runEventStream,
        transientProgress,
        removeOptimisticPendingEvent: (eventId: string) => {
            setOptimisticPendingEvents((current) =>
                current.filter(
                    (optimisticEvent) =>
                        optimisticEvent.pendingEvent.id !== eventId,
                ),
            );
        },
        resetStreamState: () => {
            setOptimisticPendingEvents([]);
        },
        stageOptimisticUserMessage: (
            pendingEvent: PhoebeAgentRunPendingEventRead,
            runId: string,
        ) => {
            setOptimisticPendingEvents((current) => [
                ...current,
                { runId, pendingEvent },
            ]);
        },
    };
}
